/*
  PONT JETONS CSS → CANVAS (audit UI 16/07, lot 2).

  LE PROBLÈME : un graphique est peint dans un <canvas>. Le canvas ne connaît
  pas le CSS : on ne peut pas lui passer `var(--ndoe-gold)`. Résultat, les 17
  graphiques de l'app portaient 75 couleurs ÉCRITES EN DUR, et zéro jeton.

  PIRE — et c'est ce qui a motivé ce module : les couleurs figées dans
  DashboardAdmin.jsx (#C9A227, #0B1F44, #6F8B74, #5A6A7E) sont exactement les
  valeurs de l'ANCIEN jeu de jetons d'index.css, celui qui PERDAIT la cascade
  et qui a été supprimé au lot 1. Les graphiques étaient donc peints dans un or
  #C9A227 que plus rien d'autre n'utilisait, à côté d'une interface en #c28a27.
  Deux ors différents côte à côte, sur le même écran.

  LA SOLUTION : lire les jetons À L'EXÉCUTION depuis le CSS (getComputedStyle
  sur :root) et les donner à Chart.js. Un seul endroit à changer le jour où la
  charte bascule vers le bleu/violet du logo (direction A) : les graphiques
  suivront TOUT SEULS.

  Volontairement sans dépendance et sans état : une fonction, un objet.
*/

// Valeurs de repli, alignées sur ndoeTokens.css (le fichier qui gagne). Elles
// ne servent que si getComputedStyle est indisponible (rendu serveur, test) :
// mieux vaut un graphique aux bonnes couleurs qu'un graphique transparent.
const REPLI = {
  ink: '#071c3d',
  royal: '#11356B',
  gold: '#c28a27',
  goldBright: '#e7c46a',
  text: '#263850',
  muted: '#6b7688',
  border: 'rgba(220, 208, 188, .8)',
  hairline: '#eee8de',
  paper: '#fffdf8',
  success: '#28784b',
  danger: '#b4231f',
  warn: '#c97a1a',
  info: '#2f5ba8',
};

const CORRESPONDANCE = {
  ink: '--ndoe-ink',
  royal: '--ndoe-royal',
  gold: '--ndoe-gold',
  goldBright: '--ndoe-gold-bright',
  text: '--ndoe-text',
  muted: '--ndoe-muted',
  border: '--ndoe-border',
  hairline: '--ndoe-hairline',
  paper: '--ndoe-paper',
  success: '--ndoe-success',
  danger: '--ndoe-danger',
  warn: '--ndoe-warn',
  info: '--ndoe-info',
};

/** Lit les jetons de la charte tels qu'ils sont RÉELLEMENT appliqués. */
export function jetons() {
  if (typeof window === 'undefined' || !window.getComputedStyle) return { ...REPLI };
  const css = window.getComputedStyle(document.documentElement);
  const out = {};
  Object.entries(CORRESPONDANCE).forEach(([cle, variable]) => {
    const v = css.getPropertyValue(variable).trim();
    out[cle] = v || REPLI[cle];
  });
  return out;
}

/**
 * Couleur opaque -> même couleur translucide, pour les aires sous les courbes.
 * Gère #rgb, #rrggbb et rgb()/rgba(). Rend la couleur telle quelle si le format
 * est inconnu : jamais de `undefined` dans un dataset.
 */
export function alpha(couleur, a = 0.12) {
  const c = String(couleur || '').trim();
  const hex = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((x) => x + x).join('') : hex[1];
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  }
  const rgb = c.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const [r, g, b] = rgb[1].split(',').map((x) => parseFloat(x));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return c;
}

/**
 * Palette CATÉGORIELLE (camemberts, barres par catégorie).
 * Ordonnée pour rester distinguable : on alterne les familles plutôt que de
 * dégrader une seule teinte — un dégradé de dorés est illisible sur un
 * téléphone en plein soleil, et indistinct pour un daltonien.
 */
export function palette() {
  const t = jetons();
  return [t.royal, t.gold, t.success, t.info, t.warn, t.goldBright, t.muted, t.danger];
}

/**
 * Options Chart.js communes : grille, axes, légende, info-bulles, animation.
 * À FUSIONNER avec les options propres au graphique (voir usage).
 *
 * ANIMATION : 420 ms, sobre et courte. Un graphique qui met 1 s à se dessiner
 * donne l'impression d'une app lente sur un téléphone d'entrée de gamme — et
 * le personnel de Ndoé consulte ces écrans des dizaines de fois par jour.
 * `prefers-reduced-motion` est respecté : animation coupée si l'utilisateur
 * l'a demandé au niveau du système.
 */
export function optionsBase({ axeY = true } = {}) {
  const t = jetons();
  const reduit = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: reduit ? false : { duration: 420, easing: 'easeOutQuart' },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10, boxHeight: 10, usePointStyle: true,
          color: t.muted, font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: t.ink,
        titleColor: t.paper,
        bodyColor: t.paper,
        borderColor: t.gold,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    scales: axeY ? {
      x: {
        grid: { display: false },
        ticks: { color: t.muted, font: { size: 10.5 } },
        border: { color: t.hairline },
      },
      y: {
        beginAtZero: true,
        grid: { color: t.hairline, drawTicks: false },
        ticks: { color: t.muted, font: { size: 10.5 }, padding: 6 },
        border: { display: false },
      },
    } : undefined,
  };
}

/** Fusion superficielle suffisante pour les options Chart.js (2 niveaux). */
export function fusionner(base, sur = {}) {
  const out = { ...base, ...sur };
  ['plugins', 'scales', 'interaction', 'animation'].forEach((k) => {
    if (base[k] && sur[k] && typeof base[k] === 'object' && typeof sur[k] === 'object') {
      out[k] = { ...base[k], ...sur[k] };
      Object.keys(base[k]).forEach((sk) => {
        if (base[k][sk] && sur[k][sk] && typeof base[k][sk] === 'object' && typeof sur[k][sk] === 'object') {
          out[k][sk] = { ...base[k][sk], ...sur[k][sk] };
        }
      });
    }
  });
  return out;
}
